// The editor screen: left = controls (path toggle, wizard OR uploader, theme),
// right = live brochure preview. Owns persistence (autosave + publish).

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEvent, saveEventDetails, publishEvent, listEventSubscriptions, listEventRegistrations } from "../../firebase/db.js";
import { removeHeroImage, resolvePageUrls, uploadSocialCard, urlFor } from "../../firebase/storage.js";
import { getEventType } from "../../eventTypes/index.js";
import { PUBLIC_BASE_URL } from "../../firebase/config.js";
import { useAuth } from "../auth/AuthContext.jsx";
import { normalizeRegistrationFields } from "../registration/formConfig.js";
import Wizard from "./Wizard.jsx";
import ImageUploader from "./ImageUploader.jsx";
import ThemePicker from "./ThemePicker.jsx";
import BackgroundPicker, { CustomBackgroundPicker } from "./BackgroundPicker.jsx";
import Brochure from "../brochure/Brochure.jsx";
import SocialCardPreview, { captureSocialCard } from "./SocialCardPreview.jsx";

function normalizeEventDefaults(event) {
  if (!event) return event;
  return {
    ...event,
    details: {
      collectPreRegistration: true,
      ...(event.details || {}),
    },
  };
}

const blankEvent = (slug, eventType) => ({
  slug, eventType, status: "draft",
  source: "build", details: { collectPreRegistration: true }, imagePages: [],
  theme: { paletteId: "midnight" },
});

export default function Editor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading, configured } = useAuth();

  const [event, setEvent] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [subscriptions, setSubscriptions] = useState(null);
  const [registrations, setRegistrations] = useState(null);
  const [previewFocus, setPreviewFocus] = useState("cover");
  const [saveState, setSaveState] = useState("idle");
  const [viewMode, setViewMode] = useState("split");
  const [previewDevice, setPreviewDevice] = useState("mobile");
  const saveTimer = useRef(null);
  const socialCardTimer = useRef(null);
  const socialCardRefreshSeq = useRef(0);
  const socialCardRef = useRef(null);

  // Load (or, in preview/unconfigured mode, start blank).
  useEffect(() => {
    if (loading) return;
    (async () => {
      if (!configured) { setEvent(blankEvent(slug, "conference")); return; }
      if (!user) { navigate(`/app/claim/${slug}`, { replace: true }); return; }
      const e = await getEvent(slug);
      const next = normalizeEventDefaults(e || blankEvent(slug, "conference"));
      setEvent(next);
      if (e && e.details?.collectPreRegistration === undefined) {
        await saveEventDetails(slug, {
          details: next.details,
          source: next.source,
          imagePages: next.imagePages,
          theme: next.theme,
          eventType: next.eventType,
        });
      }
    })();
  }, [slug, user, loading, configured]);

  // Resolve image URLs for live preview when on the images path.
  useEffect(() => {
    if (event?.imagePages?.length) {
      resolvePageUrls(event.imagePages).then(setImageUrls).catch(() => setImageUrls([]));
    } else {
      setImageUrls([]);
    }
  }, [event?.imagePages]);

  useEffect(() => {
    if (!configured || !user || !event?.details?.collectPreRegistration) {
      setSubscriptions(null);
      return;
    }
    listEventSubscriptions(slug).then(setSubscriptions).catch(() => setSubscriptions([]));
  }, [configured, user, slug, event?.details?.collectPreRegistration]);

  useEffect(() => {
    const fields = normalizeRegistrationFields(event?.details?.eventRegistrationFields || []);
    if (!configured || !user || !event?.details?.collectEventRegistrations || !fields.length) {
      setRegistrations(null);
      return;
    }
    listEventRegistrations(slug).then(setRegistrations).catch(() => setRegistrations([]));
  }, [configured, user, slug, event?.details?.collectEventRegistrations, event?.details?.eventRegistrationFields]);

  const eventType = getEventType(event?.eventType) || getEventType("conference");

  useEffect(() => {
    return () => {
      clearTimeout(saveTimer.current);
      clearTimeout(socialCardTimer.current);
    };
  }, []);

  function derivePublishOg(next) {
    const type = getEventType(next?.eventType) || getEventType("conference");
    const og = type.deriveOg(next?.details || {});
    const firstImage = imageUrls[0] || (next?.imagePages?.[0] ? urlFor(next.imagePages[0]) : "");
    if (next?.source === "images" && firstImage) og.image = firstImage;
    return og;
  }

  async function refreshSocialCard(next, { allowFallbackPublish = false } = {}) {
    const og = derivePublishOg(next);
    try {
      const blob = await captureSocialCard(socialCardRef.current);
      const uploaded = await uploadSocialCard(slug, blob);
      og.image = new URL(uploaded.url, PUBLIC_BASE_URL).toString();
    } catch (err) {
      console.warn("Social card capture failed; falling back to server renderer.", err);
      if (!allowFallbackPublish) return null;
    }
    await publishEvent(slug, og);
    setEvent((current) => current?.slug === next?.slug ? { ...current, og } : current);
    return og;
  }

  function schedulePublishedSocialCardRefresh(next) {
    if (next?.status !== "published" || !configured || !user) return;
    clearTimeout(socialCardTimer.current);
    const seq = ++socialCardRefreshSeq.current;
    socialCardTimer.current = setTimeout(async () => {
      if (seq !== socialCardRefreshSeq.current) return;
      await refreshSocialCard(next).catch((err) => {
        console.warn("Could not refresh published social card.", err);
      });
    }, 1200);
  }

  // Debounced autosave of details/source/theme/imagePages.
  function patch(next) {
    setEvent(next);
    if (!configured || !user) return;
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    clearTimeout(socialCardTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveEventDetails(slug, {
          details: next.details,
          source: next.source,
          imagePages: next.imagePages,
          theme: next.theme,
          eventType: next.eventType,
        });
        setSaveState("saved");
        schedulePublishedSocialCardRefresh(next);
      } catch {
        setSaveState("error");
      }
    }, 700);
  }

  async function switchToDynamicBackground({ backgroundId, backgroundSetId }) {
    const currentCustomBackground = event?.theme?.customBackground;
    if (currentCustomBackground?.path) {
      const confirmed = window.confirm(
        "Switching to a dynamic background will permanently remove the uploaded custom image/video from this invite. Continue?"
      );
      if (!confirmed) return;
      try {
        await removeHeroImage(currentCustomBackground.path);
      } catch (err) {
        console.warn("Could not remove custom background asset.", err);
        window.alert("We couldn't remove the uploaded media from the server, so the background was not changed.");
        return;
      }
    }

    patch({
      ...event,
      theme: {
        ...event.theme,
        backgroundId,
        backgroundSetId,
        customBackground: null,
      },
    });
  }

  async function persistEvent(next) {
    await saveEventDetails(slug, {
      details: next.details,
      source: next.source,
      imagePages: next.imagePages,
      theme: next.theme,
      eventType: next.eventType,
    });
  }

  // Build a preview event object (inject resolved URLs for image source).
  const previewEvent = useMemo(() => {
    if (!event) return null;
    return { ...event, imagePageUrls: imageUrls };
  }, [event, imageUrls]);

  async function onPublish() {
    if (configured && user) {
      clearTimeout(saveTimer.current);
      clearTimeout(socialCardTimer.current);
      await persistEvent(event);
      await refreshSocialCard(event, { allowFallbackPublish: true });
    }
    navigate(`/app/published/${slug}`);
  }

  if (!event) return <div className="container" style={{ paddingTop: 80 }}>Loading…</div>;

  return (
    <div className={`editor-shell editor-view-${viewMode}`}>
      <div style={{ position: "fixed", left: -10000, top: 0, width: 1200, height: 630, pointerEvents: "none", opacity: 0 }}>
        <SocialCardPreview ref={socialCardRef} event={previewEvent} og={derivePublishOg(event)} imageUrls={imageUrls} />
      </div>
      {/* Always-visible topbar with view toggle */}
      <div className="editor-topbar">
        <div className="editor-topbar-title">
          <span className="muted" style={{ fontSize: ".8rem" }}>einvite.to/{slug}</span>
          <span className="muted" style={{ fontSize: ".75rem" }}>
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : saveState === "error" ? "Save failed" : ""}
          </span>
        </div>
        <div className="editor-view-switch" aria-label="Editor layout">
          <button
            type="button"
            className={viewMode === "editor" ? "btn" : "btn btn-ghost"}
            onClick={() => setViewMode("editor")}
          >
            Edit
          </button>
          <button
            type="button"
            className={viewMode === "split" ? "btn desktop-only" : "btn btn-ghost desktop-only"}
            onClick={() => setViewMode("split")}
          >
            Split
          </button>
          <button
            type="button"
            className={viewMode === "preview" ? "btn" : "btn btn-ghost"}
            onClick={() => setViewMode("preview")}
          >
            Preview
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="editor-panel">
        <div className="row">
          <div>
            <h2 style={{ margin: 0 }}>Edit event</h2>
          </div>
          <div className="spacer" />
        </div>

        <div className="stack" style={{ gap: 20, marginTop: 20 }}>
          <div className="stack" style={{ gap: 6 }}>
            <h3 style={{ margin: 0 }}>Theme first</h3>
            <p className="muted" style={{ margin: 0 }}>
              Choose the invite look up front, then adjust the content around it.
            </p>
          </div>

          <ThemePicker
            paletteId={event.theme?.paletteId}
            onChange={(paletteId) => patch({ ...event, theme: { ...event.theme, paletteId } })}
          />

          <CustomBackgroundPicker
            slug={slug}
            customBackground={event.theme?.customBackground}
            onCustomChange={(customBackground) =>
              patch({
                ...event,
                theme: {
                  ...event.theme,
                  customBackground,
                  backgroundId: customBackground?.path ? null : event.theme?.backgroundId,
                  backgroundSetId: customBackground?.path ? null : event.theme?.backgroundSetId,
                },
              })
            }
          />

          <BackgroundPicker
            customBackground={event.theme?.customBackground}
            backgroundId={event.theme?.backgroundId}
            backgroundSetId={event.theme?.backgroundSetId}
            onChange={switchToDynamicBackground}
          />
        </div>

        {/* Path toggle */}
        <div className="row editor-source-switch" style={{ marginTop: 20, gap: 8 }}>
          <button
            className={event.source === "build" ? "btn" : "btn-ghost btn"}
            onClick={() => patch({ ...event, source: "build" })}
          >Build with wizard</button>
          <button
            className={event.source === "images" ? "btn" : "btn-ghost btn"}
            onClick={() => patch({ ...event, source: "images" })}
          >Upload images</button>
        </div>

        <hr style={{ borderColor: "var(--app-border)", margin: "20px 0" }} />

        {event.source === "build" ? (
          <Wizard
            eventType={eventType}
            details={event.details}
            onChange={(details) => patch({ ...event, details })}
            onFocusPage={setPreviewFocus}
            slug={slug}
          />
        ) : (
          <ImageUploader
            slug={slug}
            imagePages={event.imagePages}
            onChange={(imagePages) => patch({ ...event, imagePages })}
          />
        )}

        {event.details?.collectEventRegistrations && normalizeRegistrationFields(event.details?.eventRegistrationFields || []).length > 0 && (
          <div className="card stack" style={{ marginTop: 20, padding: 16 }}>
            <div className="row">
              <strong>Event registrations</strong>
              <div className="spacer" />
              <button
                type="button"
                className="btn-ghost btn mini"
                onClick={() => listEventRegistrations(slug).then(setRegistrations).catch(() => setRegistrations([]))}
              >
                ↻
              </button>
            </div>
            {registrations === null && <p className="muted" style={{ margin: 0 }}>Loading registrations…</p>}
            {registrations?.length === 0 && <p className="muted" style={{ margin: 0 }}>No event registrations yet.</p>}
            {!!registrations?.length && (
              <div className="stack" style={{ gap: 8 }}>
                {registrations.map((registration) => (
                  <div key={registration.id} className="subscriber-row">
                    <strong>{new Date(registration.createdAt).toLocaleString()}</strong>
                    {normalizeRegistrationFields(event.details?.eventRegistrationFields || []).map((field) => {
                      const value = registration.values?.[field.id];
                      if (!value) return null;
                      return field.kind === "image" ? (
                        <a key={field.id} href={urlFor(value)} target="_blank" rel="noreferrer">{field.label}</a>
                      ) : (
                        <span key={field.id}><strong>{field.label}:</strong> {value}</span>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {event.details?.collectPreRegistration && (
          <div className="card stack" style={{ marginTop: 20, padding: 16 }}>
            <div className="row">
              <strong>Update subscribers</strong>
              <div className="spacer" />
              <button
                type="button"
                className="btn-ghost btn mini"
                onClick={() => listEventSubscriptions(slug).then(setSubscriptions).catch(() => setSubscriptions([]))}
              >
                ↻
              </button>
            </div>
            {subscriptions === null && <p className="muted" style={{ margin: 0 }}>Loading subscribers…</p>}
            {subscriptions?.length === 0 && <p className="muted" style={{ margin: 0 }}>No subscribers yet.</p>}
            {!!subscriptions?.length && (
              <div className="stack" style={{ gap: 8 }}>
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="subscriber-row">
                    <strong>{new Date(sub.createdAt).toLocaleString()}</strong>
                    {sub.mobile && <span>{sub.mobile}</span>}
                    {sub.email && <span>{sub.email}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button className="btn" style={{ marginTop: 28, width: "100%" }} onClick={onPublish}>
          Publish & get link
        </button>
        <p className="muted center" style={{ fontSize: ".8rem", marginTop: 8 }}>
          {PUBLIC_BASE_URL.replace(/^https?:\/\//, "")}/{slug}
        </p>
      </div>

      {/* Live preview */}
      <div className="preview-panel">
        <div className="preview-toolbar" aria-label="Preview device">
          <button
            type="button"
            className={previewDevice === "mobile" ? "btn mini" : "btn btn-ghost mini"}
            onClick={() => setPreviewDevice("mobile")}
          >
            Mobile
          </button>
          <button
            type="button"
            className={previewDevice === "desktop" ? "btn mini" : "btn btn-ghost mini"}
            onClick={() => setPreviewDevice("desktop")}
          >
            Desktop
          </button>
        </div>
        <div className={`preview-stage preview-stage-${previewDevice}`}>
          <div className="preview-device-frame">
            <Brochure event={previewEvent} focusKind={previewFocus} style={{ height: "100%" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
